import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { jobAPI } from '../../services/api';
import './JobShareModal.css';

const JobShareModal = ({ isOpen, onClose, jobId }) => {
  const [shareLinks, setShareLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchShareLinks();
    } else {
      // Reset state when modal closes
      setShareLinks(null);
      setError(null);
      setCopiedLink(false);
    }
  }, [isOpen, jobId]);

  const fetchShareLinks = async () => {
    if (!jobId) {
      setError('Job ID is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await jobAPI.getShareLinks(jobId);
      console.log('Share links response:', response); // Debug log
      if (response.success) {
        setShareLinks(response.data);
      } else {
        setError(response.message || 'Failed to fetch share links');
      }
    } catch (err) {
      console.error('Error fetching share links:', err);
      setError(err.message || 'Failed to fetch share links');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareLinks?.encrypted_link) return;
    
    try {
      await navigator.clipboard.writeText(shareLinks.encrypted_link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy link. Please copy manually.');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="job-share-modal-overlay" onClick={onClose}>
      <div className="job-share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-share-modal-header">
          <h2>Share Job</h2>
          <button 
            className="job-share-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="job-share-modal-content">
          {loading ? (
            <div className="job-share-modal-loading">
              <div className="spinner"></div>
              <span>Loading share link...</span>
            </div>
          ) : error ? (
            <div className="job-share-modal-error">
              <p>{error}</p>
              <button onClick={fetchShareLinks} className="retry-btn">Retry</button>
            </div>
          ) : shareLinks ? (
            <div className="job-share-modal-body">
              <p className="job-share-modal-description">
                Share this job posting with others using the link below:
              </p>
              
              <div className="job-share-link-container">
                <div className="job-share-link-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  <span>Shareable Link</span>
                </div>
                <div className="job-share-link-input-group">
                  <input
                    type="text"
                    value={shareLinks.encrypted_link || ''}
                    readOnly
                    className="job-share-link-input"
                    placeholder="Loading..."
                  />
                  <button
                    onClick={copyToClipboard}
                    className={`job-share-copy-btn ${copiedLink ? 'copied' : ''}`}
                    title="Copy link"
                  >
                    {copiedLink ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JobShareModal;

